import { ui } from "@/components/ui";
import { AlarmClockCheck, Bell, CheckCircle2 } from "lucide-react";
import type { StatefulObservationViewT } from "../../../../types/database/observations";
import type { StatefulTrackerViewT } from "../../../../types/database/trackers";
import NewEntrySwitch from "./components/NewCardSwitch";
import { useFormsToDo } from "./hooks/useFormsToDo";

interface ItemWithStatus {
	id: string;
	name: string;
	is_due: boolean;
	is_locked?: boolean;
	end_date?: string | null;
	is_complete?: boolean;
	type: "tracker" | "observation";
	data: StatefulTrackerViewT | StatefulObservationViewT;
}

interface FormsToDoProps {
	trackers: StatefulTrackerViewT[];
	observations: StatefulObservationViewT[];
}

const FormsToDo: React.FC<FormsToDoProps> = ({ trackers, observations }) => {
	const { toDo, upcoming, completed, itemCount, taskCount } = useFormsToDo(
		trackers,
		observations,
	);

	const EmptySection = ({ message }: { message: string }) => (
		<ui.Card className="bg-white">
			<ui.CardContent className="flex justify-center items-center p-8">
				<p className="text-muted-foreground text-sm text-center">
					{message}
				</p>
			</ui.CardContent>
		</ui.Card>
	);

	const renderItem = (item: ItemWithStatus) => (
		<NewEntrySwitch key={item.id} item={item} />
	);

	return (
		<div className="space-y-12">
			<section>
				<div className="flex items-center gap-2 mb-4">
					<Bell className="text-destructive" />
					<h2 className="font-bold text-slate-900 dark:text-slate-100 text-lg">
						Action Required ({toDo.length}{" "}
						form{toDo.length === 1 ? "" : "s"})
					</h2>
					{taskCount > toDo.length && (
						<span className="text-muted-foreground text-sm whitespace-nowrap">
							({taskCount} task{taskCount === 1 ? "" : "s"})
						</span>
					)}
				</div>
				<div className="flex flex-col gap-y-4">
					{toDo.length === 0
						? (
							<EmptySection message="Great job! This diary is up to date! 🎉" />
						)
						: (
							toDo.map((item) => renderItem(item))
						)}
				</div>
			</section>

			<section>
				<div className="flex items-center gap-2 mb-4">
					<AlarmClockCheck className="text-blue-500" />
					<h2 className="font-bold text-slate-900 dark:text-slate-100 text-lg">
						Up to Date ({upcoming.length} form
						{upcoming.length === 1 ? "" : "s"})
					</h2>
				</div>
				<div className="flex flex-col gap-y-4">
					{upcoming.length === 0
						? (
							<EmptySection message="No active diaries at the moment" />
						)
						: (
							upcoming.map((item) => renderItem(item))
						)}
				</div>
			</section>

			<section>
				<div className="flex items-center gap-2 mb-4">
					<CheckCircle2 className="text-green-500" />
					<h2 className="font-bold text-slate-900 dark:text-slate-100 text-lg">
						Completed ({completed.length} form
						{completed.length === 1 ? "" : "s"})
					</h2>
				</div>
				<div className="flex flex-col gap-y-4">
					{completed.length === 0
						? <EmptySection message="No completed diaries yet" />
						: (
							completed.map((item) => renderItem(item))
						)}
				</div>
			</section>
		</div>
	);
};

export default FormsToDo;
